import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { X, Pause } from "lucide-react";
import { fetchCountries } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { earnPoints } from "@/lib/usePoints";
import { storage } from "@/lib/storage";
import StorySlide from "@/components/story/StorySlide";
import Narration from "@/components/story/Narration";
import useKaraoke from "@/components/story/useKaraoke";
import { narrationFor } from "@/components/story/narration";
import { playAmbience, stopAmbience, duckAmbience, sceneFor } from "@/lib/ambience";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const fetchStory = async (code) => (await axios.get(`${API}/story/${code}`)).data;
export const fetchStoryCodes = async () => (await axios.get(`${API}/story`)).data.codes;

const SLIDE_MS = 6500;
const MIN_SLIDE_MS = 4000;
const NARRATED_MAX_MS = 45000;
const AFTER_SPEECH_MS = 700;
const HOLD_MS = 220;
const SOUND_KEY = "atlas.story.sound";
const AMBIENCE_KEY = "atlas.story.ambience";

/** Slide clock. Narrated slides last as long as the voice (progress follows the spoken words); silent ones use a fixed timer. */
function usePlayer(slides, narrated, wordProgress, speechDoneRef) {
  const count = slides.length;
  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const startRef = useRef(performance.now());
  const elapsedRef = useRef(0);
  const next = useCallback(() => setIndex((i) => Math.min(count - 1, i + 1)), [count]);
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => { elapsedRef.current = 0; startRef.current = performance.now(); speechDoneRef.current = null; setProgress(0); }, [index, speechDoneRef]);
  useEffect(() => {
    if (paused) { elapsedRef.current += performance.now() - startRef.current; return undefined; }
    startRef.current = performance.now();
    let raf;
    const tick = () => {
      const isLast = index === count - 1;
      const el = elapsedRef.current + performance.now() - startRef.current;
      if (isLast) { setProgress(1); return; }
      if (narrated) {
        const done = speechDoneRef.current;
        setProgress(done ? 1 : Math.min(0.97, wordProgress.current));
        if ((done && performance.now() - done >= AFTER_SPEECH_MS && el >= MIN_SLIDE_MS) || el >= NARRATED_MAX_MS) return next();
      } else {
        setProgress(Math.min(1, el / SLIDE_MS));
        if (el >= SLIDE_MS) return next();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [index, paused, count, next, narrated, wordProgress, speechDoneRef]);

  return { index, progress, paused, setPaused, next, prev };
}

export default function Story() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const { data: countries = [] } = useQuery({ queryKey: ["countries"], queryFn: fetchCountries });
  const { data: story, isError } = useQuery({ queryKey: ["story", code], queryFn: () => fetchStory(code), retry: false });
  const country = countries.find((c) => c.code === code);
  const [sound, setSound] = useState(() => storage.get(SOUND_KEY, "on") === "on");

  const slides = useMemo(() => {
    if (!story || !country) return [];
    const f = story.facts || {};
    const ok = (s) => !((s.key === "languages" && !f.languages) || (s.key === "climate" && !f.climate) || (s.key === "history" && !country.historical_note));
    const list = story.slides.filter(ok);
    return [...list, { key: "end", video: list[0]?.video, video_webm: list[0]?.video_webm }];
  }, [story, country]);

  const speechDoneRef = useRef(null);
  const wordProgress = useRef(0);
  const [index0, setIndex0] = useState(0);
  const currentKey = slides[index0]?.key;
  const narration = useMemo(() => (country && currentKey && currentKey !== "end" ? narrationFor(currentKey, country, story?.facts, lang, t) : ""), [country, currentKey, story, lang, t]);
  const { words, wordIndex, speaking, speak, stop, failed, supported } = useKaraoke(narration, lang, { enabled: sound, onEnd: () => { speechDoneRef.current = performance.now(); } });
  const narrated = supported && sound && !failed && words.length > 0;
  wordProgress.current = words.length ? (wordIndex + 1) / words.length : 0;

  const { index, progress, paused, setPaused, next, prev } = usePlayer(slides, narrated, wordProgress, speechDoneRef);
  useEffect(() => { setIndex0(index); }, [index]);
  const current = slides[index];

  // Voice starts with the slide (short beat so the slide has animated in), stops when it leaves.
  useEffect(() => {
    if (!narrated || !narration) return undefined;
    const id = setTimeout(speak, 250);
    return () => { clearTimeout(id); stop(); };
  }, [narration, narrated, speak, stop]);
  useEffect(() => { if (!window.speechSynthesis) return; if (paused) window.speechSynthesis.pause(); else window.speechSynthesis.resume(); }, [paused]);
  const toggleSound = () => { const on = !sound; setSound(on); storage.set(SOUND_KEY, on ? "on" : "off"); trackEvent("story_sound", { on }); if (!on) stop(); };
  const [ambience, setAmbience] = useState(() => storage.get(AMBIENCE_KEY, "on") === "on");
  const toggleAmbience = () => { const on = !ambience; setAmbience(on); storage.set(AMBIENCE_KEY, on ? "on" : "off"); trackEvent("story_ambience", { on }); if (!on) stopAmbience(); };
  useEffect(() => {
    if (!ambience || !current || current.key === "end") { stopAmbience(); return undefined; }
    playAmbience(sceneFor(current.key, country));
    return undefined;
  }, [ambience, current, country]);
  useEffect(() => { duckAmbience(speaking); }, [speaking]);
  useEffect(() => () => stopAmbience(), []);

  const close = useCallback(() => navigate(`/?c=${code}`), [navigate, code]);
  const holdRef = useRef(null);
  const interactive = (e) => e.target.closest("a,button");
  const onDown = (e) => { if (interactive(e)) return; holdRef.current = setTimeout(() => { holdRef.current = "held"; setPaused(true); }, HOLD_MS); };
  const onUp = (e) => {
    if (holdRef.current === "held") { setPaused(false); holdRef.current = null; return; }
    clearTimeout(holdRef.current); holdRef.current = null;
    if (interactive(e)) return;
    const x = (e.clientX - e.currentTarget.getBoundingClientRect().left) / e.currentTarget.clientWidth;
    (lang === "ar" ? x > 0.3 : x < 0.3) ? prev() : next();
  };

  useEffect(() => { if (code) trackEvent("story_view", { code, lang }); }, [code, lang]);
  useEffect(() => { if (current?.key === "end") { trackEvent("story_complete", { code }); earnPoints("story_watched", lang); } }, [current, code, lang]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") close(); if (e.key === "ArrowRight") next(); if (e.key === "ArrowLeft") prev(); if (e.key === " ") setPaused((p) => !p); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close, next, prev, setPaused]);

  if (isError) return <Shell onClose={close}><p className="text-white/80 text-sm" data-testid="story-unavailable">{t("story_unavailable")}</p></Shell>;
  if (!current) return <Shell onClose={close}><div className="w-8 h-8 rounded-full border-2 border-white/30 border-t-ochre animate-spin" /></Shell>;

  return (
    <Shell onClose={close} onDown={onDown} onUp={onUp}>
      {current.video && (
        <video key={current.video} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover story-video" data-testid="story-video">
          <source src={`${process.env.REACT_APP_BACKEND_URL}${current.video}`} type="video/mp4" />
          {current.video_webm && <source src={`${process.env.REACT_APP_BACKEND_URL}${current.video_webm}`} type="video/webm" />}
        </video>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/50" />
      <div className="absolute top-3 inset-x-3 flex gap-1 z-10" data-testid="story-progress">
        {slides.map((s, i) => (
          <div key={s.key} className="h-[3px] flex-1 rounded-full bg-white/25 overflow-hidden">
            <div className="h-full bg-white rounded-full" style={{ width: `${i < index ? 100 : i === index ? progress * 100 : 0}%` }} />
          </div>
        ))}
      </div>
      {paused && <div className="absolute top-8 inset-x-0 flex justify-center z-10"><Pause className="w-6 h-6 text-white/80" /></div>}
      <div key={index} className={`absolute inset-x-0 bottom-0 px-6 pt-24 flex flex-col items-start gap-4 z-10 ${current.key === "end" ? "pb-14" : "pb-28"}`} data-testid={`story-slide-${current.key}`} dir={lang === "ar" ? "rtl" : "ltr"}>
        <StorySlide slideKey={current.key} country={country} facts={story.facts} />
      </div>
      {current.key !== "end" && <Narration words={words} wordIndex={wordIndex} speaking={speaking} sound={sound} onToggleSound={toggleSound} supported={supported} ambience={ambience} onToggleAmbience={toggleAmbience} />}
      <div className="absolute bottom-0.5 inset-x-0 text-center text-[9px] text-white/35 z-30 pointer-events-none">
        {index === 0 ? t("story_tap_hint") : current.pixabay_url ? "Video: Pixabay" : ""}
      </div>
    </Shell>
  );
}

function Shell({ children, onClose, onDown, onUp }) {
  const { t } = useLang();
  return (
    <div className="fixed inset-0 z-[200] bg-black flex items-center justify-center" data-testid="story">
      <div className="relative w-full h-full sm:h-[min(100vh,900px)] sm:max-w-[460px] sm:rounded-3xl overflow-hidden bg-ink select-none touch-manipulation flex items-center justify-center" onPointerDown={onDown} onPointerUp={onUp} onPointerCancel={onUp}>
        {children}
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} onPointerDown={(e) => e.stopPropagation()} onPointerUp={(e) => e.stopPropagation()} className="absolute top-6 end-3 z-20 w-9 h-9 rounded-full bg-black/40 text-white flex items-center justify-center hover:bg-black/70 transition-colors" aria-label={t("close")} data-testid="story-close">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
